import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Alert,
  AlertTitle,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExtensionIcon from '@mui/icons-material/Extension';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import TokenIcon from '@mui/icons-material/Token';
import PolicyIcon from '@mui/icons-material/Policy';
import ApiIcon from '@mui/icons-material/Api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchemaIcon from '@mui/icons-material/Schema';

const TroubleshootingTab: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Introduction Section - Similar to other tabs */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'error.lighter', borderLeft: '5px solid', borderColor: 'error.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BugReportIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'error.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'error.dark' }}>
            Troubleshooting Guide
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Comprehensive <strong>troubleshooting solutions</strong> for common issues you may encounter when 
          using AppConfig² and working with Microsoft Entra ID application configurations.
        </Typography>
        <Typography variant="body2" color="text.primary">
          This guide covers permission issues, authentication failures, claims mapping problems, and directory 
          extension challenges. Each section provides detailed diagnostic steps, resolution procedures, and 
          prevention strategies to help you quickly resolve issues and get back to productive work.
        </Typography>
      </Paper>

      {/* Quick Reference Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: '#fff3e0', borderLeft: '5px solid', borderColor: 'warning.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: '1.5rem', mr: 1, color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'warning.dark' }}>
            Quick Issue Identification
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🔐 <strong>Permission Errors:</strong> "Access denied" or "Insufficient privileges"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🔑 <strong>Authentication Issues:</strong> Login failures or redirect problems
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              📋 <strong>Claims Missing:</strong> Expected claims not appearing in tokens
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🧩 <strong>Extension Problems:</strong> Directory extension creation or access issues
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🌐 <strong>Browser Issues:</strong> CORS errors or popup blocking
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ⚙️ <strong>Configuration Errors:</strong> Policy or application setup problems
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Troubleshooting Sections */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'error.light', pb: 1 }}>
        Common Issues & Solutions
      </Typography>

      {/* Permission Issues */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon sx={{ mr: 1, color: '#f44336' }} />
            <Typography variant="h6" color="#d32f2f">Permission Issues</Typography>
          </Box>
          
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>Symptoms</AlertTitle>
            <Typography variant="body2">
              • Unable to access certain features or see "insufficient permissions" errors<br/>
              • Cannot modify application configurations<br/>
              • Tools return authorization failures<br/>
              • Directory operations fail with access denied errors
            </Typography>
          </Alert>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Required Permissions for AppConfig²
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#d32f2f' }}>
                  Essential Application Permissions
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Application.ReadWrite.All" 
                      secondary="Read and write application registrations"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Directory.Read.All" 
                      secondary="Read directory objects (users, groups)"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Policy.ReadWrite.ApplicationConfiguration" 
                      secondary="Manage claims mapping policies"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ef6c00' }}>
                  Additional Permissions (Optional)
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Directory.ReadWrite.All" 
                      secondary="For directory extensions management"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="User.ReadWrite.All" 
                      secondary="For user provisioning features"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary="Group.ReadWrite.All" 
                      secondary="For group assignment features"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Resolution Steps
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><ErrorOutlineIcon color="error" /></ListItemIcon>
              <ListItemText
                primary="Contact your Entra ID administrator"
                secondary="Request the necessary permissions be granted to AppConfig² in your tenant"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ErrorOutlineIcon color="error" /></ListItemIcon>
              <ListItemText
                primary="Verify admin consent status"
                secondary="Ensure admin consent has been granted for all required permissions"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ErrorOutlineIcon color="error" /></ListItemIcon>
              <ListItemText
                primary="Check your role assignments"
                secondary="Verify you have Global Administrator or Application Administrator roles"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Authentication Failures */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <VpnKeyIcon sx={{ mr: 1, color: '#ff9800' }} />
            <Typography variant="h6" color="#ef6c00">Authentication Failures</Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>Common Scenarios</AlertTitle>
            <Typography variant="body2">
              • Authentication flows fail during testing<br/>
              • Redirect URI mismatch errors<br/>
              • Token acquisition failures<br/>
              • CORS or popup blocking issues
            </Typography>
          </Alert>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NetworkCheckIcon sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="subtitle1">Redirect URI Configuration Issues</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The most common cause of authentication failures is incorrect redirect URI configuration.
              </Typography>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Required Redirect URIs for AppConfig²
              </Typography>
              
              <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1, fontFamily: 'monospace', mb: 2 }}>
                https://your-appconfig-domain.com/<br/>
                https://your-appconfig-domain.com/silent-end<br/>                
                https://your-appconfig-domain.com/admin-consent-callback
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Exact match required" 
                    secondary="Redirect URIs must match exactly - no wildcards or variations allowed"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="HTTPS only" 
                    secondary="Production environments must use HTTPS redirect URIs"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Platform configuration" 
                    secondary="Add URIs to both SPA and Web platforms if testing different flows"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TokenIcon sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="subtitle1">Token Acquisition Problems</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Issues with obtaining or using access tokens and ID tokens.
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check requested scopes and permissions" 
                    secondary="Verify the application has the necessary API permissions configured"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Verify application is enabled" 
                    secondary="Ensure the application is not disabled or in staged rollout"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check conditional access policies" 
                    secondary="CA policies may block token issuance based on conditions"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Clear browser cache and cookies" 
                    secondary="Stale authentication state can cause token acquisition failures"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BuildIcon sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="subtitle1">Browser and CORS Issues</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Browser-specific issues that can prevent authentication flows from working properly.
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Enable popups for AppConfig² domain" 
                    secondary="Authentication flows often use popup windows that browsers may block"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Disable third-party cookie blocking" 
                    secondary="Some browsers block third-party cookies needed for authentication"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Try incognito/private browsing mode" 
                    secondary="Eliminates interference from extensions and cached data"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ErrorOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check browser console for errors" 
                    secondary="Look for CORS, CSP, or other security-related error messages"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Claims Mapping Policy Issues */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PolicyIcon sx={{ mr: 1, color: '#9c27b0' }} />
            <Typography variant="h6" color="#7b1fa2">Claims Mapping Policy Issues</Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>When You Might Encounter These Issues</AlertTitle>
            <Typography variant="body2">
              • Custom claims not appearing in tokens<br/>
              • Claims mapping policy creation failures<br/>
              • Policy assignment not taking effect<br/>
              • Directory extension claims missing
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Policy Creation Issues
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Verify JSON syntax" 
                    secondary="Ensure the policy JSON is valid and properly formatted"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check claim sources and transformations" 
                    secondary="Verify attribute names and transformation rules are correct"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Use correct claim types" 
                    secondary="Ensure JwtClaimType values are appropriate for your use case"
                  />
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Policy Assignment Issues
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Verify policy is assigned to application" 
                    secondary="Check that the claims mapping policy is correctly linked"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Enable acceptMappedClaims" 
                    secondary="Application must have acceptMappedClaims set to true"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ContentCopyIcon color="secondary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Wait for propagation" 
                    secondary="Changes can take 15-30 minutes to take effect"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
              Quick Verification Steps
            </Typography>
            <Typography variant="body2">
              1. Use AppConfig²'s JWT Token Decoder to examine token contents<br/>
              2. Check both ID tokens and access tokens for custom claims<br/>
              3. Verify the directory extension values exist on user objects<br/>
              4. Test with a fresh authentication session after making changes
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Directory Extension Issues */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ExtensionIcon sx={{ mr: 1, color: '#00796b' }} />
            <Typography variant="h6" color="#004d40">Directory Extension Issues</Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>Complex Permission Requirements</AlertTitle>
            <Typography variant="body2">
              Directory extensions have specific ownership and permission requirements that can be 
              confusing. This section helps clarify the most common issues.
            </Typography>
          </Alert>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Permission and Ownership Issues</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Verify required permissions" 
                    secondary="Directory.ReadWrite.All and Application.ReadWrite.All are required for extension management"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check extension ownership" 
                    secondary="You must be an owner of the application that owns the extension"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Understand Entra Connect limitations" 
                    secondary="Extensions created by Entra Connect cannot be modified directly in Entra ID"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Extension Creation Problems</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Use dedicated schema application" 
                    secondary="Create extensions in a dedicated application for better management"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Follow naming conventions" 
                    secondary="Use descriptive names and consistent patterns for extension properties"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Verify target object types" 
                    secondary="Ensure extensions target the correct object types (User, Group, etc.)"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Entra Connect Extension Issues</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Special considerations for extensions synchronized from on-premises Active Directory.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Read-only in Entra ID" 
                    secondary="Entra Connect extensions can only be modified in on-premises AD"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Check synchronization status" 
                    secondary="Verify Entra Connect is running and synchronizing properly"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><ExtensionIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Identify by naming pattern" 
                    secondary="Entra Connect extensions follow specific naming patterns"
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Understanding OAuth2PermissionGrants vs OAuth2PermissionScopes vs RequiredResourceAccess */}
      <Card sx={{ borderRadius: 2, mb: 3, mt: 3, border: '2px solid #9c27b0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SchemaIcon sx={{ mr: 1, color: '#7b1fa2', fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
              Understanding OAuth2PermissionGrants vs OAuth2PermissionScopes vs RequiredResourceAccess
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph color="text.primary">
            Understanding the relationship between <strong>OAuth2PermissionGrants</strong> (consent records), 
            <strong>OAuth2PermissionScopes</strong> (available permissions), and <strong>RequiredResourceAccess</strong> (requested permissions) 
            is essential for troubleshooting permission and consent issues.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>Quick Summary</AlertTitle>
            <Typography variant="body2">
              • <strong>OAuth2PermissionScopes</strong> = What permissions an API <em>offers</em> (defined on API's Service Principal)<br/>
              • <strong>RequiredResourceAccess</strong> = What permissions your app <em>requests</em> (configured on App Registration)<br/>
              • <strong>OAuth2PermissionGrants</strong> = What permissions have been <em>granted/consented</em> (stored in tenant directory)
            </Typography>
          </Alert>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ApiIcon sx={{ mr: 1, color: '#388e3c' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  OAuth2PermissionScopes (Available Permissions)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>OAuth2PermissionScopes</strong> are delegated permissions (scopes) that an API exposes. 
                  These define what permissions are <em>available</em> for client applications to request.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Location: Service Principal"
                      secondary="Found in the Service Principal (Enterprise Application) object, not the App Registration"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: Define Available Permissions"
                      secondary="Declare what delegated permissions (scopes) client apps can request from this API"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Examples: 'User.Read', 'Mail.Send', 'Files.Read.All'"
                      secondary="Standard OAuth 2.0 scope strings that appear in authorization requests"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                    📍 Where to View in AppConfig²
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Graph Explorer</strong> → Query Service Principal → View <code>oauth2PermissionScopes</code> property
                  </Typography>
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f1f8e9', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    GET /servicePrincipals/{'{'}id{'}'}<br/>
                    Response includes: oauth2PermissionScopes: [...]
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>Service Principals Tool</strong> → Select service principal → View "Exposed API Permissions" section
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example:</strong> Microsoft Graph API's Service Principal exposes hundreds of OAuth2PermissionScopes like 
                    "User.Read", "Mail.Send", "Calendars.ReadWrite". Your custom APIs define their own scopes like "Orders.Read", "Invoices.Write".
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <VpnKeyIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  RequiredResourceAccess (Requested Permissions)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>RequiredResourceAccess</strong> defines what permissions your application <em>requests</em> from APIs. 
                  This is the "shopping list" of permissions your app needs.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Location: App Registration"
                      secondary="Configured in the Application object under 'API Permissions' in the portal"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: Declare Permission Requirements"
                      secondary="Lists which APIs your app needs to call and what permissions it requires from each"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Triggers Admin Consent UI"
                      secondary="This list appears in the admin consent screen when administrators grant permissions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Does NOT Grant Access"
                      secondary="Just requesting permissions doesn't grant them - consent is still required"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>
                    📍 Where to View in AppConfig²
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Graph Explorer</strong> → Query Application → View <code>requiredResourceAccess</code> property
                  </Typography>
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#e1f5fe', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    GET /applications/{'{'}id{'}'}<br/>
                    Response includes: requiredResourceAccess: [{'{'}resourceAppId, resourceAccess[]{'}'}]
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>API Permissions Manager</strong> → View all requested permissions, consent status, and admin consent controls
                  </Typography>
                </Box>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> RequiredResourceAccess is declarative only. Even if you add permissions here, 
                    users/admins must still explicitly consent before your app can use them. Check OAuth2PermissionGrants to verify actual consent.
                  </Typography>
                </Alert>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example:</strong> Your app's RequiredResourceAccess might list:<br/>
                    • Microsoft Graph → User.Read, Mail.Send (delegated)<br/>
                    • Your Custom API → Orders.Read, Orders.Write (delegated)
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SecurityIcon sx={{ mr: 1, color: '#d32f2f' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  OAuth2PermissionGrants (Granted Permissions / Consent Records)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>OAuth2PermissionGrants</strong> are the actual consent records that prove permissions have been granted. 
                  These are the "receipts" showing who consented to what.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Location: Directory Objects (Separate Collection)"
                      secondary="Stored as separate objects in the directory, not on App Registration or Service Principal"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: Record Actual Consent"
                      secondary="Proves that a user or admin has consented to specific permissions for a client-resource pair"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Key Properties: clientId, resourceId, principalId, scope"
                      secondary="Links the client app, target API, consenting user (or 'AllPrincipals' for admin consent), and granted scopes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Controls Token Issuance"
                      secondary="Only permissions with valid OAuth2PermissionGrants appear in access tokens (in the 'scp' claim)"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#c62828' }}>
                    📍 Where to View in AppConfig²
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Graph Explorer</strong> → Query OAuth2PermissionGrants (two methods)
                  </Typography>
                  <Box sx={{ mt: 1, p: 1, bgcolor: '#ffcdd2', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    GET /oauth2PermissionGrants<br/>
                    Filter: ?$filter=clientId eq '{'{'}appServicePrincipalId{'}'}'<br/>
                    <br/>
                    <em>Or via Service Principal:</em><br/>
                    GET /servicePrincipals/{'{'}id{'}'}/oauth2PermissionGrants<br/>
                    <br/>
                    Response: List of consent records with scope, resourceId, principalId
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>API Permissions Manager</strong> → "Consent Status" section shows which permissions have active grants
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>Token Scope Requester</strong> → "Consent Diagnostics" button analyzes OAuth2PermissionGrants for current user/app
                  </Typography>
                </Box>

                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle sx={{ fontWeight: 600 }}>Common Troubleshooting Scenario</AlertTitle>
                  <Typography variant="body2">
                    <strong>Problem:</strong> App requests "User.Read Mail.Send" but token only contains "User.Read"<br/>
                    <strong>Diagnosis:</strong> Check OAuth2PermissionGrants - likely no grant exists for "Mail.Send"<br/>
                    <strong>Solution:</strong> User or admin must consent to "Mail.Send" permission (creates a new grant)
                  </Typography>
                </Alert>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example Grant Record:</strong><br/>
                    • clientId: Your app's Service Principal ID<br/>
                    • resourceId: Microsoft Graph Service Principal ID<br/>
                    • principalId: User's ID (or null for admin consent to "AllPrincipals")<br/>
                    • scope: "User.Read Mail.Send Calendars.Read"<br/>
                    • consentType: "Principal" (user consent) or "AllPrincipals" (admin consent)
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Visual Relationship Diagram */}
          <Box sx={{ mt: 3, p: 3, bgcolor: '#f5f5f5', borderRadius: 2, border: '2px solid #9c27b0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#7b1fa2' }}>
              🔄 How They Work Together
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#e8f5e9' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    1️⃣ API Defines Scopes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>OAuth2PermissionScopes</strong> on the API's Service Principal define what permissions exist.
                  </Typography>
                  <Chip label="API/Service Principal" size="small" color="success" sx={{ mt: 1 }} />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#e3f2fd' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                    2️⃣ App Requests Scopes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>RequiredResourceAccess</strong> on the client App Registration lists which scopes the app wants to request.
                  </Typography>
                  <Chip label="App Registration" size="small" color="primary" sx={{ mt: 1 }} />
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%', bgcolor: '#ffebee' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#c62828', mb: 1 }}>
                    3️⃣ User/Admin Grants Consent
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    <strong>OAuth2PermissionGrants</strong> records prove consent was given, enabling tokens with those scopes.
                  </Typography>
                  <Chip label="Directory Object" size="small" color="error" sx={{ mt: 1 }} />
                </Paper>
              </Grid>
            </Grid>

            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>✅ For a permission to appear in an access token:</strong><br/>
                1. API must expose it in <strong>OAuth2PermissionScopes</strong><br/>
                2. App must request it in <strong>RequiredResourceAccess</strong> (or directly in auth request)<br/>
                3. A matching <strong>OAuth2PermissionGrant</strong> must exist (consent given)
              </Typography>
            </Alert>
          </Box>

          {/* Practical Troubleshooting Tips */}
          <Box sx={{ mt: 3, p: 3, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #f57c00' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#e65100' }}>
              🔧 Troubleshooting Permission Issues
            </Typography>
            
            <Typography variant="body2" paragraph sx={{ fontWeight: 600, color: '#ef6c00' }}>
              Scenario 1: "Permission not found" error when requesting scope
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mb: 2 }}>
              • Check: Does the API's Service Principal have this scope in <strong>OAuth2PermissionScopes</strong>?<br/>
              • Tool: Graph Explorer → GET /servicePrincipals/{'{'}apiId{'}'} → Check oauth2PermissionScopes array<br/>
              • Fix: Ensure you're using the exact scope name (case-sensitive) as defined by the API
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" paragraph sx={{ fontWeight: 600, color: '#ef6c00' }}>
              Scenario 2: Scope requested but not appearing in token
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mb: 2 }}>
              • Check: Is there an <strong>OAuth2PermissionGrant</strong> record for this client-resource-user combination?<br/>
              • Tool: Graph Explorer → GET /oauth2PermissionGrants?$filter=clientId eq 'yourAppSpId'<br/>
              • Fix: User or admin must consent (creates the grant). Use "Admin consent" button in API Permissions Manager
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" paragraph sx={{ fontWeight: 600, color: '#ef6c00' }}>
              Scenario 3: Admin consent says "granted" but user still gets consent prompt
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mb: 2 }}>
              • Check: Look at the OAuth2PermissionGrant's <strong>consentType</strong> and <strong>scope</strong> fields<br/>
              • Issue: Grant might exist but with limited scopes, or consentType might be "Principal" (user-specific) not "AllPrincipals" (tenant-wide)<br/>
              • Tool: API Permissions Manager → Check "Consent Status" → Look for "Admin consent (tenant-wide)" indicator<br/>
              • Fix: Admin consent should create a grant with consentType="AllPrincipals" and principalId=null
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" paragraph sx={{ fontWeight: 600, color: '#ef6c00' }}>
              Scenario 4: Token contains MORE scopes than requested
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
              • Explanation: Entra ID returns the <strong>union</strong> of requested scopes + all previously granted scopes (from OAuth2PermissionGrants)<br/>
              • Check: Look at existing grants - they persist even after removing from RequiredResourceAccess<br/>
              • Tool: Token Scope Requester → "Requested vs Granted" section highlights extra scopes<br/>
              • Fix: To remove unwanted scopes, delete/revoke the OAuth2PermissionGrant (Enterprise Apps → User consent → Revoke)
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* General Troubleshooting Tips */}
      <Card sx={{ borderRadius: 2, bgcolor: '#f8f9fc' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">General Troubleshooting Tips</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                  Before You Start
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Check browser console"
                      secondary="Look for JavaScript errors or network failures"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Verify network connectivity"
                      secondary="Ensure you can reach Microsoft endpoints"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Test with different user"
                      secondary="Rule out user-specific permission issues"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#388e3c' }}>
                  Debugging Techniques
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Use developer tools"
                      secondary="Inspect network requests and responses"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Enable verbose logging"
                      secondary="Check for detailed error messages in tools"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Test step by step"
                      secondary="Isolate issues by testing components individually"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ef6c00' }}>
                  When to Escalate
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Tenant-wide issues"
                      secondary="Contact your Microsoft support representative"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Permission escalation needed"
                      secondary="Work with your Entra ID administrators"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Service outages"
                      secondary="Check Microsoft 365 Service Health dashboard"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Information Note */}
      <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6" color="info.dark" sx={{ fontWeight: 500 }}>
            Getting Additional Help
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          If you've tried the solutions in this guide and are still experiencing issues, consider reaching out 
          to your organization's Entra ID administrators or Microsoft support team. When reporting issues, 
          include specific error messages, screenshots, and the steps you've already attempted to resolve the problem.
        </Typography>
      </Paper>
    </Box>
  );
};

export default TroubleshootingTab;